import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/gameCenter/providers/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import cn from "classnames"

import { ImageUpload } from "components/imageUpload";

interface ProviderMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly: boolean;
}

const ProviderMainTab: React.FC<ProviderMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.providers.payload);

  const [imgPreview, setImgPreview] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isDescriptionFetching, setIsDescriptionFetching] = useState<boolean>(false);
  useEffect(() => {
    if (payload?.image) {
      if (typeof payload.image === "string") {
        setImgPreview(payload.image);
      } else {
        const file = payload?.image;
        if (file && file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setImgPreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  }, [payload]);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null
  ) => {
    if (isReadOnly) {
      return;
    }

    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const handleAIText = async () => {
    if (isReadOnly) {
      return;
    }

    setIsDescriptionFetching(true);
    try {
      const result = await instance.post(apiRoutes.generateAIText, {
        instanceType: "provider",
        attribute: "description",
        instanceId: payload?.id,
      });
      if (!description) {
        setDescription(payload?.description ?? "empty");
      }
      handleData("description", result.data?.text ?? "");
    } catch {
      console.log("handleAIText error");
    }
    setIsDescriptionFetching(false);
  };

  return (
    <div className="game-form-content">
      <div className="game-form-box">
        <div className="game-form-box--item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="game-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={isReadOnly}
          />
        </div>
        <div className="game-form-box--item">
          <InnerInput
            value={payload?.slug ?? ""}
            placeholder={t("enterSlug")}
            label={t("slug")}
            type="text"
            id="game-slug"
            onChange={(e) => {
              handleData("slug", e);
              clearFieldError("slug");
            }}
            hasError={Boolean(fieldErrors?.["slug"])}
            disabled={isReadOnly}
          />
        </div>
        <div className="game-form-box--item with-ai">
          <div className="ai-buttons">
            <Button
              handleClick={() => {
                handleData(
                  "description",
                  description === "empty" ? "" : description,
                );
                setDescription("");
              }}
              disabled={isReadOnly || !description || isDescriptionFetching}
              isSecondary
              txt="&larr;"
            />
            <Button
              disabled={isReadOnly || isDescriptionFetching}
              handleClick={handleAIText}
              txt={t("aiAssist")}
            />
          </div>
          <InnerInput
            maxCodePoints={2500}
            value={payload?.description ?? ""}
            placeholder={t("enterText")}
            label={t("description")}
            type="textarea"
            id="game-description"
            disabled={isReadOnly || isDescriptionFetching}
            onChange={(e) => {
              handleData("description", e);
              clearFieldError("description");
            }}
            hasError={Boolean(fieldErrors?.["description"])}
          />
        </div>
        <div className="game-form-check--row">
          <div
            className={cn("item badge-checkbox", {
              "is-checked": payload?.isPopular,
              disabled: isReadOnly,
            })}
            aria-disabled={isReadOnly}
            onClick={() => {
              handleData(
                "isPopular",
                payload?.isPopular ? !payload?.isPopular : true,
              );
            }}
          >
            <div
              className={`def-check-control ${
                payload?.isPopular ? "checked" : ""
              }`}
            />
            <div className="label">POPULAR</div>
          </div>
        </div>
      </div>
      <div className="game-form-box">
        <div className="game-form-img game-form-img--game center">
          {/* <div className="game-form-field">
            <div className="game-form-img-label">Icon</div>

            <div className="game-form-img game-form-img--icon">
              <div className="game-item game-item--icon">
                {iconPreview ? (
                  <div
                    className="game-item-img"
                    style={{ backgroundImage: `url(${iconPreview})` }}
                  />
                ) : (
                  <div className="game-item-loader" />
                )}

                <input
                  type="file"
                  accept="image/*"
                  className="file-input"
                  ref={iconInputRef}
                  onChange={(e) =>
                    handleData("imageIcon", "", e.target.files?.[0])
                  }
                />

                <div className="game-item-content">
                  <div className="game-item-content--actions">
                    <div
                      className="option"
                      onClick={() => iconInputRef.current?.click()}
                    >
                      <img src={editIcon} alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          <div className="game-form-field game-form-image">
            <div className="game-form-img-label provider-image-label">Image</div>

            <div className="game-item">
              <ImageUpload
                image={payload?.image}
                isActive={Boolean(payload?.isActive)}
                disabled={isReadOnly}
                onAddImage={(file) => {
                  handleData("image", "", file);
                  clearFieldError("image");
                }}
              />
              <div
                className={cn("provider-img-popular", {
                  checked: payload?.isPopular,
                })}
              />
            </div>
          </div>
          <div className="game-form-controls">
            <div className="game-form-switcher">
              <SwitchBtn
                activeTxt={t("active")}
                notActiveTxt={t("hidden")}
                isActive={!!payload?.isActive}
                disabled={isReadOnly}
                handleClick={() => {
                  handleData("isActive", !payload?.isActive);
                  clearFieldError("isActive");
                }}
                hasError={Boolean(fieldErrors?.["isActive"])}
              />
            </div>
            <div className="game-form-icon">
              <p>Icon</p>
              <ImageUpload
                image={payload?.icon}
                isActive={Boolean(payload?.isActive)}
                disabled={isReadOnly}
                onAddImage={(file) => {
                  handleData("icon", "", file);
                }}
                type="square"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderMainTab;
